import classNames from 'classnames';
import get from 'lodash.get';
import React from 'react';
import PropTypes, { string } from 'prop-types';

import { articleFragment as ArticleFragmentPropType } from 'lib/CustomPropTypes';
import { appendSearchParam } from 'lib/link';

import { LinkIfHref } from 'components/Link';

import styles from './styles.module.scss';

const block = 'showblog-navigation';
export const ShowBlogNavigation = ({
  currentArticleId,
  additionalClasses = '',
  latestArticle = null,
  nextArticle = null,
  previousArticle = null,
  showHeadlines = false,
  hideLatestOverride = false,
  isBottomNavigation = false,
}) => {
  const previousHeadline = get(previousArticle, 'headline.primary', null);
  let previousUrl = get(previousArticle, 'url.primary', null);

  const nextHeadline = get(nextArticle, 'headline.primary', null);
  let nextUrl = get(nextArticle, 'url.primary', null);
  const nextId = get(nextArticle, 'id', null);

  const hasNextAndPrevious = !!previousHeadline && !!nextHeadline;

  const latestHeadline = get(latestArticle, 'headline.primary', null);
  let latestUrl = get(latestArticle, 'url.primary', null);
  const lastId = get(latestArticle, 'id', null);

  const nextIsLatest = nextId === lastId;
  const currentIsLatest = currentArticleId === lastId;

  // don't show latest if
  // * hideLatestOverride is true
  // * when there's no next headline because that means we're already looking at the latest
  // * next article is latest article
  const showLatest = hideLatestOverride === true ? false
    : (latestHeadline && !currentIsLatest && !nextIsLatest);

  let itemClasses = '';
  let prevOnlyClasses = '';
  let nextOnlyClasses = '';
  let latestClasses = '';

  if (isBottomNavigation && hasNextAndPrevious) {
    itemClasses = 'grid-col-12 grid-col-6-m br-0 bl-0';
    latestClasses = 'grid-col-12 br-0 bl-0';
  } else if (isBottomNavigation && !hasNextAndPrevious) {
    itemClasses = `${styles.singleNavItem} grid-col-12 br-0 bl-0`;
    latestClasses = 'grid-col-12 br-0 bl-0';
  } else if (hasNextAndPrevious) {
    itemClasses = (showHeadlines) ? 'grid-col-12' : 'grid-col-6';
    itemClasses += ' grid-col-5-m'
      + ' grid-col-3_5-l grid-col-push-0-l'
      + ' grid-col-3-xl';
    prevOnlyClasses = 'grid-col-push-1-m br-0 grid-col-push-1-xl';
    nextOnlyClasses = 'bl-0';
  } else if (!hasNextAndPrevious) {
    itemClasses = `${styles.singleNavItem}`
      + ' grid-col-12'
      + ' grid-col-10-m grid-col-push-1-m '
      + ' grid-col-7-l grid-col-push-0-l'
      + ' grid-col-6-xl grid-col-push-1-xl';
  }

  const nextICID = (isBottomNavigation) ? 'nextpost_bot' : 'nextpost_top';
  nextUrl = appendSearchParam(nextUrl, { icid: nextICID });
  const prevICID = (isBottomNavigation) ? 'previouspost_bot' : 'previouspost_top';
  previousUrl = appendSearchParam(previousUrl, { icid: prevICID });
  const latestICID = 'latestpost_bot';
  latestUrl = appendSearchParam(latestUrl, { icid: latestICID });

  return (
    <nav
      aria-label="Posts"
      className={
        classNames(
          block,
          styles.showBlogNav,
          additionalClasses,
          { [styles.isBottom]: isBottomNavigation },
          { [styles.isTop]: !isBottomNavigation },
        )
      }
      data-testid={block}
    >
      {previousHeadline && (
        <div
          className={
            classNames(
              styles.navItem,
              styles.previousItem,
              'layout-grid-item',
              itemClasses,
              prevOnlyClasses,
            )
          }
          data-testid={`${block}-prev`}
        >
          <div className={styles.navItemLink}>
            <LinkIfHref
              href={previousUrl}
              icid={prevICID}
              rel="prev"
            >
              <span className={`${styles.icon} icon icon-chevron-left`} />
              Previous Post
            </LinkIfHref>
          </div>
          {showHeadlines && (
            <div
              className={styles.navItemHeadline}
              data-testid={`${block}-headline`}
            >
              <LinkIfHref
                href={previousUrl}
                icid={prevICID}
                rel="prev"
              >
                {previousHeadline}
              </LinkIfHref>
            </div>
          )}
        </div>
      )}
      {nextHeadline && (
        <div
          className={
            classNames(
              styles.navItem,
              styles.isNext,
              'layout-grid-item',
              itemClasses,
              nextOnlyClasses,
            )
          }
          data-testid={`${block}-next`}
        >
          <div
            className={styles.navItemLink}
          >
            <LinkIfHref
              href={nextUrl}
              icid={nextICID}
              rel="next"
            >
              {nextIsLatest ? 'Latest Post' : 'Next Post' }
              <span className={`${styles.icon} icon icon-chevron-right`} />
            </LinkIfHref>
          </div>
          {showHeadlines && (
            <div
              className={styles.navItemHeadline}
              data-testid={`${block}-headline`}
            >
              <LinkIfHref
                href={nextUrl}
                icid={nextICID}
                rel="next"
              >
                {nextHeadline}
              </LinkIfHref>
            </div>
          )}
        </div>
      )}

      {showLatest && (
        <div
          className={
            classNames(
              styles.navItem,
              styles.latestItem,
              'layout-grid-item',
              latestClasses,
            )
          }
          data-testid={`${block}-latest`}
        >
          <div className={styles.navItemLink}>
            <LinkIfHref
              href={latestUrl}
              icid={latestICID}
            >
              Latest Post
            </LinkIfHref>
          </div>
          {showHeadlines && (
            <div
              className={styles.navItemHeadline}
              data-testid={`${block}-headline`}
            >
              <LinkIfHref
                href={latestUrl}
                icid={latestICID}
              >
                {latestHeadline}
              </LinkIfHref>
            </div>
          )}
        </div>
      )}
    </nav>
  );
};

ShowBlogNavigation.propTypes = {
  additionalClasses: string,
  currentArticleId: PropTypes.string.isRequired,
  latestArticle: ArticleFragmentPropType,
  nextArticle: ArticleFragmentPropType,
  previousArticle: ArticleFragmentPropType,
  showHeadlines: PropTypes.bool,
  hideLatestOverride: PropTypes.bool,
  isBottomNavigation: PropTypes.bool,
};
